/**
 * The typeface and color a firm's name is set in (2026-09-30).
 *
 * ─── WHY A CATALOG AND NOT A FONT NAME ──────────────────────────────────────
 * A font only renders if the page has loaded it. A free-text font name would
 * look right on the designer's Mac, where the font happens to be installed,
 * and silently fall back to something else on the client's phone — the one
 * screen the branding exists for. So the choice is a KEY into this list, every
 * entry of which the app can actually load.
 *
 * Cormorant Garamond and Inter are already in index.html. The rest are fetched
 * from Google Fonts the first time a brand asks for one, which is why
 * `googleFamily` is part of the entry rather than a lookup somewhere else.
 *
 * ─── AND WHY THE COLOR IS CHECKED ───────────────────────────────────────────
 * The name sits on white, in the header, on the client's share page and on
 * printed pages. Pale gold on white is a perfectly reasonable thing to pick
 * from a color wheel and an unreadable thing to publish, so `colorWarning()`
 * says so before it is saved. It warns; it does not refuse. It is their brand.
 */

export type BrandFontKey = 'cormorant' | 'playfair' | 'dm_serif' | 'inter' | 'jost';

export interface BrandFont {
  key: BrandFontKey;
  label: string;
  /** Full CSS stack, with a same-category fallback for a font that won't load. */
  stack: string;
  /** Google Fonts family + weights, or null when index.html already loads it. */
  googleFamily: string | null;
  note: string;
}

export const DEFAULT_BRAND_FONT: BrandFontKey = 'cormorant';

export const BRAND_FONTS: readonly BrandFont[] = [
  {
    key: 'cormorant', label: 'Cormorant Garamond', googleFamily: null,
    stack: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
    note: 'The default — a fine-lined classical serif.',
  },
  {
    key: 'playfair', label: 'Playfair Display', googleFamily: 'Playfair+Display:wght@400;500',
    stack: "'Playfair Display', Georgia, 'Times New Roman', serif",
    note: 'High contrast and editorial. Reads well large.',
  },
  {
    key: 'dm_serif', label: 'DM Serif Display', googleFamily: 'DM+Serif+Display',
    stack: "'DM Serif Display', Georgia, 'Times New Roman', serif",
    note: 'Solid and warm, heavier than the others.',
  },
  {
    key: 'inter', label: 'Inter', googleFamily: null,
    stack: "Inter, system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
    note: 'Plain modern sans — the app’s own body typeface.',
  },
  {
    key: 'jost', label: 'Jost', googleFamily: 'Jost:wght@300;400;500',
    stack: "Jost, 'Futura', 'Century Gothic', system-ui, sans-serif",
    note: 'Geometric sans, in the Futura family of shapes.',
  },
];

const BY_KEY = new Map(BRAND_FONTS.map((f) => [f.key, f] as const));

/** The chosen font, or the default for an unknown, empty or missing key. */
export function brandFont(key: string | null | undefined): BrandFont {
  return BY_KEY.get((key ?? '').trim() as BrandFontKey) ?? BY_KEY.get(DEFAULT_BRAND_FONT)!;
}

/** CSS `font-family` value for a brand. Never empty. */
export function brandFontStack(key: string | null | undefined): string {
  return brandFont(key).stack;
}

/**
 * The Google Fonts URL a brand needs, or null when nothing has to be fetched.
 *
 * `display=swap` deliberately: the name renders immediately in the fallback
 * and swaps when the file lands. A header that is blank for 400ms is worse
 * than one that reflows.
 */
export function brandFontHref(key: string | null | undefined): string | null {
  const family = brandFont(key).googleFamily;
  return family ? `https://fonts.googleapis.com/css2?family=${family}&display=swap` : null;
}

// ─── Color ──────────────────────────────────────────────────────────────────

/** The app's ink, used when a firm hasn't chosen. */
export const DEFAULT_BRAND_COLOR = '#1A1714';

const HEX = /^#[0-9a-f]{6}$/i;

/** A usable #RRGGBB, or the default. Accepts a bare `RRGGBB` and shorthand. */
export function brandColor(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (raw === '') return DEFAULT_BRAND_COLOR;
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (HEX.test(withHash)) return withHash.toUpperCase();
  // #abc → #aabbcc, which is what a color input's older cousins emit.
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(withHash);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toUpperCase();
  return DEFAULT_BRAND_COLOR;
}

/** Relative luminance, per WCAG 2.x. */
function luminance(hex: string): number {
  const n = parseInt(brandColor(hex).slice(1), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

/** Contrast ratio between two colors, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * What to say about a chosen color, or null when it's fine.
 *
 * 3:1 is WCAG's bar for large text, which is what a company name is
 * everywhere it appears. Below that it is not a style choice, it is a name the
 * client cannot read.
 */
export function colorWarning(value: string | null | undefined, background = '#FFFFFF'): string | null {
  const ratio = contrastRatio(brandColor(value), background);
  if (ratio >= 3) return null;
  return ratio >= 2
    ? 'That color is faint against white — your name may be hard to read on a client’s screen or in print.'
    : 'That color is nearly invisible against white. Your name won’t be readable on a client’s screen or in print.';
}
